import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios"; 
import PublicNavbar from "../components/PublicNavbar";
import { Eye, EyeOff, AlertTriangle } from "lucide-react";

const colors = {
  primary: "#03f8d7",
  accent: "#ff2d75",
  deepBg: "#060608",
  surface: "rgba(13, 13, 18, 0.95)",
  textMain: "#ffffff",
  textMuted: "#64748b",
  border: "rgba(255, 255, 255, 0.08)",
};

function Signup() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    inviteCode: "", // Registration Key
    withdrawPassword: "", // Security PIN
  });

  const [showPass, setShowPass] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeField, setActiveField] = useState(null);

  // UI Effect: Light turns on for specific fields
  const lightTriggerFields = ['name', 'email', 'inviteCode'];
  const isLightOn = lightTriggerFields.includes(activeField);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // 1. Client-side validation
    if (form.password !== form.confirmPassword) {
      setError("PASSWORDS DO NOT MATCH");
      return;
    }

    if (form.withdrawPassword.length < 4) {
      setError("SETTLEMENT PIN TOO SHORT");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      /**
       * FIX: Removed 'res' assignment to resolve ESLint 'no-unused-vars' build failure.
       * This ensures the production build on Vercel/Koyeb succeeds.
       */
      await api.post("/api/auth/signup", {
        name: form.name,
        email: form.email,
        password: form.password,
        registrationKey: form.inviteCode, 
        securityPin: form.withdrawPassword, 
      });

      // 2. Success Logic: Redirect to Pending Approval
      navigate("/pending-approval");
      
    } catch (err) {
      console.error("Signup Error:", err);
      const message = err.response?.data?.message || "SYSTEM OFFLINE: CONNECTION FAILED";
      setError(message.toUpperCase());
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <style>
        {`
          .signup-page {
            min-height: 100vh;
            background-color: ${colors.deepBg};
            display: flex;
            flex-direction: column;
            font-family: 'Inter', sans-serif;
          }

          .content-wrapper {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;
          }

          .signup-card {
            width: 100%;
            max-width: 460px;
            background: ${colors.surface};
            padding: 40px 40px 50px 40px;
            border-radius: 28px;
            border: 1px solid ${colors.border};
            box-shadow: 0 40px 100px rgba(0,0,0,0.8);
            text-align: center;
            position: relative;
            overflow: hidden;
          }

          .card-bg-logo {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 80%;
            opacity: 0.08;
            filter: blur(10px);
            pointer-events: none;
            z-index: 0;
          }

          .signup-title {
            position: relative;
            z-index: 2;
            font-size: 22px;
            font-weight: 800;
            letter-spacing: 6px;
            margin-top: -10px;
            margin-bottom: 40px;
            text-transform: uppercase;
            color: #fff;
            opacity: ${isLightOn ? '0' : '1'};
            transform: translateY(${isLightOn ? '-30px' : '0'});
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .card-light-strip {
            position: absolute;
            top: 0; left: 0; right: 0;
            height: 3px;
            background: ${colors.primary};
            box-shadow: 0 0 20px ${colors.primary};
            opacity: ${isLightOn ? '1' : '0'};
            transition: opacity 0.3s ease;
            z-index: 3;
          }

          .card-light-beam {
            position: absolute;
            top: 0; left: 10%; right: 10%;
            height: 300px;
            background: linear-gradient(to bottom, rgba(3, 248, 215, 0.12), transparent);
            clip-path: polygon(15% 0%, 85% 0%, 100% 100%, 0% 100%);
            opacity: ${isLightOn ? '1' : '0'};
            transition: opacity 0.5s ease;
            pointer-events: none;
            z-index: 1;
          }

          .form-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            z-index: 2;
            position: relative;
          }

          .input-group {
            position: relative;
            margin-bottom: 20px;
            text-align: left;
            z-index: 2;
          }

          .full-width { grid-column: span 2; }

          .input-field {
            width: 100%;
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid ${colors.border};
            border-radius: 12px;
            padding: 15px 18px;
            color: white;
            font-size: 14px;
            outline: none;
            transition: 0.3s;
            box-sizing: border-box;
          }

          .input-field:focus {
            border-color: ${isLightOn ? colors.primary : colors.textMuted};
            background: rgba(255, 255, 255, 0.05);
          }

          .label-text {
            position: absolute;
            left: 18px;
            top: 50%;
            transform: translateY(-50%);
            color: ${colors.textMuted};
            pointer-events: none;
            transition: 0.3s;
            font-size: 13px;
          }

          .input-field:focus + .label-text,
          .input-field:not(:placeholder-shown) + .label-text {
            top: -10px;
            left: 5px;
            font-size: 10px;
            color: ${colors.primary};
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1px;
          }

          .signup-btn {
            width: 100%;
            padding: 16px;
            background: ${colors.primary};
            color: #000;
            border: none;
            border-radius: 12px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 2px;
            cursor: pointer;
            margin-top: 10px;
            transition: 0.3s;
          }

          .signup-btn:hover:not(:disabled) {
            filter: brightness(1.1);
            box-shadow: 0 0 20px rgba(3, 248, 215, 0.3);
          }

          .error-toast {
            background: rgba(255, 45, 117, 0.15);
            border: 1px solid ${colors.accent};
            color: ${colors.accent};
            padding: 12px;
            border-radius: 10px;
            margin-bottom: 20px;
            font-size: 11px;
            font-weight: 800;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            animation: slideDown 0.3s ease;
          }

          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .footer-nav {
            margin-top: 25px;
            font-size: 13px;
            color: ${colors.textMuted};
          }

          .nav-link { 
            color: #fff; 
            font-weight: 700; 
            cursor: pointer; 
            margin-left: 5px;
            text-decoration: underline;
            text-underline-offset: 3px;
          }
        `}
      </style>

      <PublicNavbar />

      <div className="content-wrapper">
        <div className="signup-card">
          <div className="card-light-strip" />
          <div className="card-light-beam" />
          <img src="/loginbg.png" alt="logo" className="card-bg-logo" />

          <h1 className="signup-title">Registration Panel</h1>

          {error && (
            <div className="error-toast">
              <AlertTriangle size={12} />
              {error}
            </div>
          )}

          <form onSubmit={handleSignup} className="form-grid">
            <div className="input-group full-width">
              <input
                className="input-field"
                name="name"
                placeholder=" "
                value={form.name}
                onFocus={() => setActiveField('name')}
                onBlur={() => setActiveField(null)}
                onChange={handleChange}
                required
              />
              <label className="label-text">Enter Your Name</label>
            </div>

            <div className="input-group full-width">
              <input
                className="input-field"
                name="email"
                type="email"
                placeholder=" "
                value={form.email}
                onFocus={() => setActiveField('email')}
                onBlur={() => setActiveField(null)}
                onChange={handleChange}
                required
              />
              <label className="label-text">Enter Your Email</label>
            </div>

            <div className="input-group">
              <input
                className="input-field"
                name="password"
                type={showPass ? "text" : "password"}
                placeholder=" "
                value={form.password}
                onFocus={() => setActiveField('password')}
                onBlur={() => setActiveField(null)}
                onChange={handleChange}
                required
              />
              <label className="label-text">Create Password</label>
              <div 
                style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: colors.textMuted}}
                onClick={() => setShowPass(!showPass)}
              >
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </div>
            </div>

            <div className="input-group">
              <input
                className="input-field"
                name="confirmPassword"
                type="password"
                placeholder=" "
                value={form.confirmPassword}
                onFocus={() => setActiveField('confirmPassword')}
                onBlur={() => setActiveField(null)}
                onChange={handleChange}
                required
              />
              <label className="label-text">Verify Password</label>
            </div>

            <div className="input-group full-width">
              <input
                className="input-field"
                style={{textAlign: 'center', letterSpacing: '3px', fontWeight: 'bold', borderColor: colors.primary}}
                name="inviteCode"
                placeholder=" "
                value={form.inviteCode}
                onFocus={() => setActiveField('inviteCode')}
                onBlur={() => setActiveField(null)}
                onChange={handleChange}
                required
              />
              <label className="label-text" style={{width: '100%', textAlign: 'center', left: 0}}>Global Entry Code</label>
            </div>

            <div className="input-group full-width">
              <input
                className="input-field"
                name="withdrawPassword"
                type={showPin ? "text" : "password"}
                placeholder=" "
                value={form.withdrawPassword}
                onFocus={() => setActiveField('withdrawPassword')}
                onBlur={() => setActiveField(null)}
                onChange={handleChange}
                required
              />
              <label className="label-text">Security PIN (6-Digits)</label>
              <div 
                style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: colors.textMuted}}
                onClick={() => setShowPin(!showPin)}
              >
                {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
              </div>
            </div>

            <button className="signup-btn" type="submit" disabled={loading} style={{gridColumn: 'span 2'}}>
              {loading ? "INITIALIZING..." : "CONFIRM REGISTERATION"}
            </button>
          </form>

          <div className="footer-nav">
            Already a Member? 
            <span className="nav-link" onClick={() => navigate("/login")}> Login Now</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;